/**
 * AnyClip — 하단 배너 (알리 상품 슬라이더) + 중앙 스폰서배너
 */

import React, { useState, useEffect } from 'react';
import { useI18n } from '../context/I18nContext';
import { useApp } from '../context/AppContext';
import AliProductCard from './AliProductCard';
import './AdBanner.css';

// CPA 오퍼 설정 (실제 배포 시 네트워크 오퍼 링크로 교체)
const AD_CONFIG = {
  bottom: {
    links: [
      'https://www.fishing-go.com',
      'https://play.google.com/store/apps/details?id=com.dailyfilm.app'
    ],
    label: '📢 광고 문의: @sunjulab',
    inquiryLink: 'https://t.me/sunjulab',
  },
  center: {
    imageUrl: 'https://placehold.co/300x250/1a1a3e/FF6B9D?text=CPA+Offer+300x250',
    link: 'https://www.cj.com',
    label: '🎁 Special Offer',
  },
};

function AdBanner({ position = 'bottom', visible = true }) {
  const [isClosed, setIsClosed] = useState(false);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [ogCache, setOgCache] = useState({});
  const [isLoadingOg, setIsLoadingOg] = useState(position === 'bottom');
  const [fadeClass, setFadeClass] = useState('fade-in');
  const { t } = useI18n();
  const config = AD_CONFIG[position];

  const currentLink = position === 'bottom' && config.links ? config.links[currentAdIndex] : config.link;

  // visible이 다시 true가 될 때 닫힘 상태 초기화
  useEffect(() => {
    if (visible) setIsClosed(false);
  }, [visible]);

  // 10초마다 롤링
  useEffect(() => {
    if (position !== 'bottom' || !config.links || config.links.length <= 1) return;

    const interval = setInterval(() => {
      setFadeClass('fade-out'); // 애니메이션 시작
      setTimeout(() => {
        setCurrentAdIndex((prev) => (prev + 1) % config.links.length);
        setFadeClass('fade-in'); // 애니메이션 끝
      }, 300); // 0.3초 대기 후 교체
    }, 10000); // 10초마다 전환

    return () => clearInterval(interval);
  }, [position, config.links]);

  // Open Graph 데이터 가져오기 (캐싱)
  useEffect(() => {
    if (position === 'bottom' && currentLink) {
      if (ogCache[currentLink]) {
        setIsLoadingOg(false);
        return;
      }

      setIsLoadingOg(true);
      fetch(`/api/og-data?url=${encodeURIComponent(currentLink)}`)
        .then((res) => {
          if (!res.ok) throw new Error('API Response not OK');
          return res.json();
        })
        .then((data) => {
          setOgCache((prev) => ({ ...prev, [currentLink]: data }));
          setIsLoadingOg(false);
        })
        .catch((err) => {
          console.error('OG Fetch Error:', err);
          setOgCache((prev) => ({
            ...prev,
            [currentLink]: {
              title: t('ad_sponsor'),
              description: t('ref_desc2'),
              image: 'https://placehold.co/120x120/1a1a3e/ffffff?text=APP',
            }
          }));
          setIsLoadingOg(false);
        });
    }
  }, [position, currentLink, ogCache]);

  const ogData = position === 'bottom' ? ogCache[currentLink] : null;

  if (!visible || isClosed) return null;

  const handleAdClick = () => {
    const targetLink = position === 'bottom' ? currentLink : config.link;
    if (window.Telegram?.WebApp?.openLink) {
      window.Telegram.WebApp.openLink(targetLink);
    } else {
      window.open(targetLink, '_blank', 'noopener,noreferrer');
    }
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setIsClosed(true);
  };

  const adHtml = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <style>
          body { margin: 0; padding: 0; background: transparent; display: flex; justify-content: center; align-items: center; overflow: hidden; }
        </style>
      </head>
      <body>
        <script type="text/javascript">
          atOptions = {
            'key' : 'b8d11a6781112013945244e41f3a2bd9',
            'format' : 'iframe',
            'height' : 250,
            'width' : 300,
            'params' : {}
          };
        </script>
        <script type="text/javascript" src="https://www.highperformanceformat.com/b8d11a6781112013945244e41f3a2bd9/invoke.js"></script>
      </body>
    </html>
  `;

  return (
    <div
      className={`ad-banner ad-banner--${position}`}
      role="complementary"
      aria-label="광고"
    >
      <div className="ad-header-row">
        {position === 'bottom' && config.inquiryLink ? (
          <button 
            className="ad-label ad-inquiry-link" 
            onClick={(e) => {
              e.stopPropagation();
              if (window.Telegram?.WebApp?.openLink) {
                window.Telegram.WebApp.openLink(config.inquiryLink);
              } else {
                window.open(config.inquiryLink, '_blank', 'noopener,noreferrer');
              }
            }}
          >
            {position === 'bottom' ? t('ad_inquiry') : config.label}
          </button>
        ) : (
          <span className="ad-label">{position === 'bottom' ? t('ad_sponsor') : config.label}</span>
        )}
        {position === 'center' && (
          <button className="ad-banner__close-btn" onClick={handleClose} aria-label="광고 닫기">
            ✕
          </button>
        )}
      </div>
      
      {position === 'bottom' ? (
        /* 하단 배너: 알리익스프레스 상품 슬라이더 */
        <div className="ali-banner-slider">
          <div className="ali-banner-header">
            <span className="ali-banner-title">{t('today_deals')}</span>
          </div>
          <div className="ali-banner-track">
            {[0,1,2,3,4].map(i => (
              <AliProductCard
                key={i}
                type={i % 2 === 0 ? 'banner' : 'mission2'}
                telegramId={window?.Telegram?.WebApp?.initDataUnsafe?.user?.id || 'dev'}
                compact={true}
              />
            ))}
          </div>
        </div>
      ) : (

        <div 
          className="ad-banner__center-wrapper" 
          style={{ width: '300px', height: '250px', background: '#1a1a3e', display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}
        >
          <iframe
            title="Sponsor Ad"
            srcDoc={adHtml}
            width="300"
            height="250"
            style={{ border: 'none', overflow: 'hidden', width: '100%', height: '100%' }}
            scrolling="no"
            sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          />
        </div>
      )}
    </div>
  );
}

export default AdBanner;
